import { useLazyQuery, useMutation } from '@apollo/client'
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material'
import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import {
    type GetCandidatesByNameQuery,
    type GetCandidateMergePreviewQuery,
    type MergeCandidatesMutation,
} from '__generated__/graphql'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { recruiterRouteConfig } from 'recruiter/routes'
import CandidatesAutocompleteSingle from 'shared/components/CandidatesAutocomplete/CandidatesAutocompleteSingle'
import useToast from 'shared/components/Toast/useToast'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { getFullName } from 'shared/utils'
import { getPxAsRem } from 'shared/utils'
import { GetCandidateMergePreview, MergeCandidates } from './CandidateMerge.gql'

type CandidateOption =
    GetCandidatesByNameQuery['candidatesByName']['data'][number]

type CandidateData = NonNullable<GetCandidateMergePreviewQuery['candidate']>

type MergeResultData = NonNullable<MergeCandidatesMutation['mergeCandidates']>

function truncate(text: string, max: number): string {
    if (text.length <= max) return text
    return text.slice(0, max) + '…'
}

function formatTestScores(
    scores:
        | {
              satTotal?: number | null
              actScore?: number | null
              gmatScore?: number | null
              greTotalScore?: number | null
          }
        | null
        | undefined
): string | null {
    if (!scores) return null
    const parts: string[] = []
    if (scores.satTotal) parts.push(`SAT: ${scores.satTotal}`)
    if (scores.actScore) parts.push(`ACT: ${scores.actScore}`)
    if (scores.gmatScore) parts.push(`GMAT: ${scores.gmatScore}`)
    if (scores.greTotalScore) parts.push(`GRE: ${scores.greTotalScore}`)
    return parts.length > 0 ? parts.join(', ') : null
}

interface StatRowConfig {
    label: string
    key: string
    expandable?: boolean
    getPreviewDisplay: (data: CandidateData) => string | null
    getResultDisplay: (data: MergeResultData) => string | null
    previewHasValue: (data: CandidateData) => boolean
    resultHasValue: (data: MergeResultData) => boolean
}

const STAT_ROWS: StatRowConfig[] = [
    {
        label: 'Bio',
        key: 'bio',
        getPreviewDisplay: (d) => (d.bio?.bio ? truncate(d.bio.bio, 60) : null),
        getResultDisplay: (d) => (d.bio?.bio ? truncate(d.bio.bio, 60) : null),
        previewHasValue: (d) => !!d.bio,
        resultHasValue: (d) => !!d.bio,
    },
    {
        label: 'Compensation',
        key: 'compensation',
        getPreviewDisplay: (d) =>
            d.compensation?.compensation
                ? truncate(d.compensation.compensation, 60)
                : null,
        getResultDisplay: (d) =>
            d.compensation?.compensation
                ? truncate(d.compensation.compensation, 60)
                : null,
        previewHasValue: (d) => !!d.compensation,
        resultHasValue: (d) => !!d.compensation,
    },
    {
        label: 'Employment Type',
        key: 'employmentType',
        getPreviewDisplay: (d) => d.employmentType?.value ?? null,
        getResultDisplay: (d) => d.employmentType?.value ?? null,
        previewHasValue: (d) => !!d.employmentType,
        resultHasValue: (d) => !!d.employmentType,
    },
    {
        label: 'Gender',
        key: 'gender',
        getPreviewDisplay: (d) => d.gender?.gender?.value ?? null,
        getResultDisplay: (d) => d.gender?.gender?.value ?? null,
        previewHasValue: (d) => !!d.gender,
        resultHasValue: (d) => !!d.gender,
    },
    {
        label: 'HIG Model Test',
        key: 'higModelTest',
        getPreviewDisplay: (d) => d.higModelTest?.higModelTest?.value ?? null,
        getResultDisplay: (d) => d.higModelTest?.higModelTest?.value ?? null,
        previewHasValue: (d) => !!d.higModelTest,
        resultHasValue: (d) => !!d.higModelTest,
    },
    {
        label: 'HIG Referral',
        key: 'higReferral',
        getPreviewDisplay: (d) => d.higReferral?.higReferral?.value ?? null,
        getResultDisplay: (d) => d.higReferral?.higReferral?.value ?? null,
        previewHasValue: (d) => !!d.higReferral,
        resultHasValue: (d) => !!d.higReferral,
    },
    {
        label: 'Hometown',
        key: 'hometown',
        getPreviewDisplay: (d) => d.hometown?.city ?? null,
        getResultDisplay: (d) => d.hometown?.city ?? null,
        previewHasValue: (d) => !!d.hometown,
        resultHasValue: (d) => !!d.hometown,
    },
    {
        label: 'Interview Bucket',
        key: 'interviewBucket',
        getPreviewDisplay: (d) => d.interviewBucket?.bucket ?? null,
        getResultDisplay: (d) => d.interviewBucket?.bucket ?? null,
        previewHasValue: (d) => !!d.interviewBucket,
        resultHasValue: (d) => !!d.interviewBucket,
    },
    {
        label: 'LGBTQ+',
        key: 'lgbtq',
        getPreviewDisplay: (d) => d.lgbtq?.lgbtq?.value ?? null,
        getResultDisplay: (d) => d.lgbtq?.lgbtq?.value ?? null,
        previewHasValue: (d) => !!d.lgbtq,
        resultHasValue: (d) => !!d.lgbtq,
    },
    {
        label: 'Outreach Status',
        key: 'outreachStatus',
        getPreviewDisplay: (d) => d.outreachStatus?.status ?? null,
        getResultDisplay: (d) => d.outreachStatus?.status ?? null,
        previewHasValue: (d) => !!d.outreachStatus,
        resultHasValue: (d) => !!d.outreachStatus,
    },
    {
        label: 'Personality Scores',
        key: 'personalityScores',
        getPreviewDisplay: (d) =>
            d.personalityScores?.ccatPercentile != null
                ? `CCAT: ${d.personalityScores.ccatPercentile}%`
                : null,
        getResultDisplay: (d) =>
            d.personalityScores?.ccatPercentile != null
                ? `CCAT: ${d.personalityScores.ccatPercentile}%`
                : null,
        previewHasValue: (d) => !!d.personalityScores,
        resultHasValue: (d) => !!d.personalityScores,
    },
    {
        label: 'Recruiting Status',
        key: 'recruitingStatus',
        getPreviewDisplay: (d) => d.recruitingStatus?.status ?? null,
        getResultDisplay: (d) => d.recruitingStatus?.status ?? null,
        previewHasValue: (d) => !!d.recruitingStatus,
        resultHasValue: (d) => !!d.recruitingStatus,
    },
    {
        label: 'Start Dates',
        key: 'startDates',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.startDate?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.startDates?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.startDate?.length ?? 0) > 0,
        resultHasValue: (d) => (d.startDates?.length ?? 0) > 0,
    },
    {
        label: 'System Status',
        key: 'systemStatus',
        getPreviewDisplay: (d) => d.systemStatus?.status ?? null,
        getResultDisplay: (d) => d.systemStatus?.status ?? null,
        previewHasValue: (d) => !!d.systemStatus,
        resultHasValue: (d) => !!d.systemStatus,
    },
    {
        label: 'Test Scores',
        key: 'testScores',
        getPreviewDisplay: (d) => formatTestScores(d.testScores),
        getResultDisplay: (d) => formatTestScores(d.testScores),
        previewHasValue: (d) => !!d.testScores,
        resultHasValue: (d) => !!d.testScores,
    },
    {
        label: 'Tier',
        key: 'tier',
        getPreviewDisplay: (d) => d.tier?.tier?.value ?? null,
        getResultDisplay: (d) => d.tier?.tier?.value ?? null,
        previewHasValue: (d) => !!d.tier,
        resultHasValue: (d) => !!d.tier,
    },
    {
        label: 'Analysts',
        key: 'analysts',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.otherAnalysts?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.analysts?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.otherAnalysts?.length ?? 0) > 0,
        resultHasValue: (d) => (d.analysts?.length ?? 0) > 0,
    },
    {
        label: 'Citizenships',
        key: 'citizenships',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.citizenships?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.citizenships?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.citizenships?.length ?? 0) > 0,
        resultHasValue: (d) => (d.citizenships?.length ?? 0) > 0,
    },
    {
        label: 'CPI Placed',
        key: 'cpiPlaced',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.cpiPlaced?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.cpiPlaced?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.cpiPlaced?.length ?? 0) > 0,
        resultHasValue: (d) => (d.cpiPlaced?.length ?? 0) > 0,
    },
    {
        label: 'Deal Sourcing Roles',
        key: 'dealSourcingRoles',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = (d as any).dealSourcingRoles?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = (d as any).dealSourcingRoles?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => ((d as any).dealSourcingRoles?.length ?? 0) > 0,
        resultHasValue: (d) => ((d as any).dealSourcingRoles?.length ?? 0) > 0,
    },
    {
        label: 'Ethnicities',
        key: 'ethnicities',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.ethnicities?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.ethnicities?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.ethnicities?.length ?? 0) > 0,
        resultHasValue: (d) => (d.ethnicities?.length ?? 0) > 0,
    },
    {
        label: 'Languages',
        key: 'languages',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.languages?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.languages?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.languages?.length ?? 0) > 0,
        resultHasValue: (d) => (d.languages?.length ?? 0) > 0,
    },
    {
        label: 'MBA Tiers',
        key: 'mbaTiers',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.mbaTiers?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.mbaTiers?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.mbaTiers?.length ?? 0) > 0,
        resultHasValue: (d) => (d.mbaTiers?.length ?? 0) > 0,
    },
    {
        label: 'Personality Attributes',
        key: 'personalityAttributes',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.personalityAttributes?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.personalityAttributes?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.personalityAttributes?.length ?? 0) > 0,
        resultHasValue: (d) => (d.personalityAttributes?.length ?? 0) > 0,
    },
    {
        label: 'Programs',
        key: 'programs',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.programs?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.programs?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.programs?.length ?? 0) > 0,
        resultHasValue: (d) => (d.programs?.length ?? 0) > 0,
    },
    {
        label: 'Real Estate Deal Exp.',
        key: 'realEstateDealExperiences',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.realEstateDealExperiences?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.realEstateDealExperiences?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.realEstateDealExperiences?.length ?? 0) > 0,
        resultHasValue: (d) => (d.realEstateDealExperiences?.length ?? 0) > 0,
    },
    {
        label: 'Revenue Experiences',
        key: 'revenueExperiences',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.revenueExperiences?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.revenueExperiences?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.revenueExperiences?.length ?? 0) > 0,
        resultHasValue: (d) => (d.revenueExperiences?.length ?? 0) > 0,
    },
    {
        label: 'Staffers',
        key: 'staffers',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.staffers?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.staffers?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.staffers?.length ?? 0) > 0,
        resultHasValue: (d) => (d.staffers?.length ?? 0) > 0,
    },
    {
        label: 'Veteran Statuses',
        key: 'veteranStatuses',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.veteranStatuses?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.veteranStatuses?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.veteranStatuses?.length ?? 0) > 0,
        resultHasValue: (d) => (d.veteranStatuses?.length ?? 0) > 0,
    },
    {
        label: 'Visa Statuses',
        key: 'visaStatuses',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.visaStatuses?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.visaStatuses?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.visaStatuses?.length ?? 0) > 0,
        resultHasValue: (d) => (d.visaStatuses?.length ?? 0) > 0,
    },
    {
        label: 'Industry Preferences',
        key: 'industryPreferences',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.industryPreferences?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.industryPreferences?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.industryPreferences?.length ?? 0) > 0,
        resultHasValue: (d) => (d.industryPreferences?.length ?? 0) > 0,
    },
    {
        label: 'Sector Preferences',
        key: 'sectorPreferences',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.sectorPreferences?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.sectorPreferences?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.sectorPreferences?.length ?? 0) > 0,
        resultHasValue: (d) => (d.sectorPreferences?.length ?? 0) > 0,
    },
    {
        label: 'Location Preferences',
        key: 'locationPreferences',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.locationPreferences?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.locationPreferences?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.locationPreferences?.length ?? 0) > 0,
        resultHasValue: (d) => (d.locationPreferences?.length ?? 0) > 0,
    },
    {
        label: 'Company Preferences',
        key: 'companyPreferences',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.companyPreferences?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.companyPreferences?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.companyPreferences?.length ?? 0) > 0,
        resultHasValue: (d) => (d.companyPreferences?.length ?? 0) > 0,
    },
    {
        label: 'Documents',
        key: 'documents',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.documents?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.documents?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.documents?.length ?? 0) > 0,
        resultHasValue: (d) => (d.documents?.length ?? 0) > 0,
    },
    {
        label: 'Education',
        key: 'education',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.education?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.education?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.education?.length ?? 0) > 0,
        resultHasValue: (d) => (d.education?.length ?? 0) > 0,
    },
    {
        label: 'Notes',
        key: 'candidateNotes',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.candidateNotes?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.candidateNotes?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.candidateNotes?.length ?? 0) > 0,
        resultHasValue: (d) => (d.candidateNotes?.length ?? 0) > 0,
    },
    {
        label: 'Group Notes',
        key: 'candidateGroupNotes',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.candidateGroupNotes?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.candidateGroupNotes?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.candidateGroupNotes?.length ?? 0) > 0,
        resultHasValue: (d) => (d.candidateGroupNotes?.length ?? 0) > 0,
    },
    {
        label: 'References',
        key: 'references',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.references?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.references?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.references?.length ?? 0) > 0,
        resultHasValue: (d) => (d.references?.length ?? 0) > 0,
    },
    {
        label: 'Work Experience',
        key: 'workExperience',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.workExperience?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.workExperience?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.workExperience?.length ?? 0) > 0,
        resultHasValue: (d) => (d.workExperience?.length ?? 0) > 0,
    },
    {
        label: 'Reference Attribute',
        key: 'referenceAttribute',
        getPreviewDisplay: (d) =>
            (d as any).referenceAttribute?.referenceAttribute?.value ?? null,
        getResultDisplay: (d) =>
            (d as any).referenceAttribute?.referenceAttribute?.value ?? null,
        previewHasValue: (d) => !!(d as any).referenceAttribute,
        resultHasValue: (d) => !!(d as any).referenceAttribute,
    },
    {
        label: 'Rosters',
        key: 'rosters',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.rosters?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.rosters?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.rosters?.length ?? 0) > 0,
        resultHasValue: (d) => (d.rosters?.length ?? 0) > 0,
    },
    {
        label: 'Job Board Responses',
        key: 'jobBoardsResponses',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.jobBoardsResponses?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.jobBoardsResponses?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.jobBoardsResponses?.length ?? 0) > 0,
        resultHasValue: (d) => (d.jobBoardsResponses?.length ?? 0) > 0,
    },
    {
        label: 'HIG Teams',
        key: 'higTeams',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.higTeams?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.higTeams?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.higTeams?.length ?? 0) > 0,
        resultHasValue: (d) => (d.higTeams?.length ?? 0) > 0,
    },
    {
        label: 'Schedules',
        key: 'schedules',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.schedules?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.schedules?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.schedules?.length ?? 0) > 0,
        resultHasValue: (d) => (d.schedules?.length ?? 0) > 0,
    },
    {
        label: 'EPP Scores',
        key: 'eppScores',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.eppScores?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.eppScores?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.eppScores?.length ?? 0) > 0,
        resultHasValue: (d) => (d.eppScores?.length ?? 0) > 0,
    },
    {
        label: 'Client Contacts',
        key: 'companyClientContacts',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.companyClientContacts?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.companyClientContacts?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.companyClientContacts?.length ?? 0) > 0,
        resultHasValue: (d) => (d.companyClientContacts?.length ?? 0) > 0,
    },
    {
        label: 'Roster Client Contacts',
        key: 'rosterClientContacts',
        expandable: true,
        getPreviewDisplay: (d) => {
            const count = d.rosterClientContacts?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        getResultDisplay: (d) => {
            const count = d.rosterClientContacts?.length ?? 0
            return count > 0 ? `${count}` : null
        },
        previewHasValue: (d) => (d.rosterClientContacts?.length ?? 0) > 0,
        resultHasValue: (d) => (d.rosterClientContacts?.length ?? 0) > 0,
    },
]

function getCandidateDisplayName(candidate: CandidateOption): string {
    return candidate.user ? getFullName(candidate.user) : '(unknown)'
}

export default function CandidateMergePage() {
    usePageTitle('Merge Candidates')
    const toast = useToast()
    const navigate = useNavigate()

    const [leftCandidate, setLeftCandidate] = useState<CandidateOption | null>(
        null
    )
    const [rightCandidate, setRightCandidate] =
        useState<CandidateOption | null>(null)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const [fetchLeft, { data: leftData }] = useLazyQuery(
        GetCandidateMergePreview,
        { fetchPolicy: 'network-only' }
    )
    const [fetchRight, { data: rightData }] = useLazyQuery(
        GetCandidateMergePreview,
        { fetchPolicy: 'network-only' }
    )

    const [mergePreview, { data: previewData, loading: previewing }] =
        useMutation(MergeCandidates, { fetchPolicy: 'no-cache' })

    const [mergeCandidates, { loading: merging, client }] = useMutation(
        MergeCandidates,
        {
            onCompleted: () => {
                client.cache.evict({ fieldName: 'candidate' })
                client.cache.evict({ fieldName: 'candidates' })
                client.cache.evict({ fieldName: 'candidatesByName' })
                client.cache.gc()
                toast({
                    title: 'Success',
                    severity: 'success',
                    message: 'Candidates merged successfully',
                })
                navigate(
                    `${recruiterRouteConfig.candidates.path}/${rightCandidate!.id}`
                )
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: error.message,
                })
            },
        }
    )

    const handleLeftSelect = (_: unknown, value: CandidateOption | null) => {
        setLeftCandidate(value)
        if (value) fetchLeft({ variables: { id: value.id } })
    }

    const handleRightSelect = (_: unknown, value: CandidateOption | null) => {
        setRightCandidate(value)
        if (value) fetchRight({ variables: { id: value.id } })
    }

    useEffect(() => {
        if (
            leftCandidate &&
            rightCandidate &&
            leftCandidate.id !== rightCandidate.id
        ) {
            mergePreview({
                variables: {
                    winnerId: rightCandidate.id,
                    loserId: leftCandidate.id,
                    liveRun: false,
                },
            })
        }
    }, [leftCandidate, rightCandidate])

    const leftName = leftCandidate
        ? getCandidateDisplayName(leftCandidate)
        : null
    const rightName = rightCandidate
        ? getCandidateDisplayName(rightCandidate)
        : null

    return (
        <Stack
            direction="column"
            spacing={2}
            sx={{
                padding: getPxAsRem(16, 24),
                height: '100%',
                overflow: 'auto',
            }}
        >
            <Typography variant="h2">Merge Candidates</Typography>
            <Stack direction="row" spacing={2}>
                <Card sx={{ flex: 1, p: getPxAsRem(16) }}>
                    <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        Candidate A (will be archived)
                    </Typography>
                    <CandidatesAutocompleteSingle
                        value={leftCandidate}
                        onChange={handleLeftSelect}
                        getOptionDisabled={(option) =>
                            option.id === rightCandidate?.id
                        }
                        label="Search candidate"
                        size="small"
                    />
                </Card>
                <Card sx={{ flex: 1, p: getPxAsRem(16) }}>
                    <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                    >
                        Candidate B (survivor)
                    </Typography>
                    <CandidatesAutocompleteSingle
                        value={rightCandidate}
                        onChange={handleRightSelect}
                        getOptionDisabled={(option) =>
                            option.id === leftCandidate?.id
                        }
                        label="Search candidate"
                        size="small"
                    />
                </Card>
            </Stack>
            <ComparisonTable
                leftCandidate={leftData?.candidate ?? undefined}
                rightCandidate={rightData?.candidate ?? undefined}
                mergeResult={previewData?.mergeCandidates ?? undefined}
                previewing={previewing}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={
                        !leftCandidate ||
                        !rightCandidate ||
                        leftCandidate.id === rightCandidate.id ||
                        merging
                    }
                    onClick={() => setConfirmOpen(true)}
                >
                    {leftName && rightName
                        ? `Merge "${leftName}" into "${rightName}"`
                        : 'Select two candidates to merge'}
                </Button>
            </Box>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Merge</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will transfer all associations from{' '}
                        <strong>{leftName}</strong> into{' '}
                        <strong>{rightName}</strong> and archive {leftName}.
                        This action cannot be easily undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            setConfirmOpen(false)
                            mergeCandidates({
                                variables: {
                                    winnerId: rightCandidate!.id,
                                    loserId: leftCandidate!.id,
                                    liveRun: true,
                                },
                            })
                        }}
                        variant="contained"
                        color="error"
                    >
                        Merge
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}

function ComparisonTable({
    leftCandidate,
    rightCandidate,
    mergeResult,
    previewing,
}: {
    leftCandidate?: CandidateData
    rightCandidate?: CandidateData
    mergeResult?: MergeResultData
    previewing: boolean
}) {
    const hasData = leftCandidate || rightCandidate
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

    const toggleRow = (key: string) => {
        setExpandedRows((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const leftName = leftCandidate?.user
        ? getFullName(leftCandidate.user)
        : undefined
    const rightName = rightCandidate?.user
        ? getFullName(rightCandidate.user)
        : undefined

    return (
        <TableContainer
            component={Card}
            sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}
        >
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: '25%' }}>
                            Association
                        </TableCell>
                        <TableCell
                            align="right"
                            sx={{ fontWeight: 600, width: '25%' }}
                        >
                            {leftName ?? 'Candidate A'}
                        </TableCell>
                        <TableCell
                            align="right"
                            sx={{ fontWeight: 600, width: '25%' }}
                        >
                            {rightName ?? 'Candidate B'}
                        </TableCell>
                        <TableCell
                            align="right"
                            sx={(theme) => ({
                                fontWeight: 600,
                                width: '25%',
                                bgcolor:
                                    theme.palette.mode === 'dark'
                                        ? 'grey.800'
                                        : 'grey.100',
                            })}
                        >
                            Merge Result
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!hasData ? (
                        <TableRow>
                            <TableCell
                                colSpan={4}
                                align="center"
                                sx={{ py: 4, color: 'text.secondary' }}
                            >
                                Select two candidates to compare
                            </TableCell>
                        </TableRow>
                    ) : (
                        STAT_ROWS.map((row) => {
                            const leftHas = leftCandidate
                                ? row.previewHasValue(leftCandidate)
                                : false
                            const rightHas = rightCandidate
                                ? row.previewHasValue(rightCandidate)
                                : false
                            const resultHas = mergeResult
                                ? row.resultHasValue(mergeResult)
                                : false
                            const allEmpty = !leftHas && !rightHas && !resultHas

                            const leftDisplay = leftCandidate
                                ? row.getPreviewDisplay(leftCandidate)
                                : null
                            const rightDisplay = rightCandidate
                                ? row.getPreviewDisplay(rightCandidate)
                                : null
                            const resultDisplay = mergeResult
                                ? row.getResultDisplay(mergeResult)
                                : null

                            const transferred = !rightHas && resultHas
                            const isExpanded = expandedRows.has(row.key)
                            const canExpand = row.expandable && !allEmpty

                            return (
                                <StatRow
                                    key={row.key}
                                    config={row}
                                    leftDisplay={leftDisplay}
                                    rightDisplay={rightDisplay}
                                    resultDisplay={resultDisplay}
                                    allEmpty={allEmpty}
                                    hasLeft={!!leftCandidate}
                                    hasRight={!!rightCandidate}
                                    previewing={previewing}
                                    hasResult={!!mergeResult}
                                    transferred={transferred}
                                    canExpand={!!canExpand}
                                    isExpanded={isExpanded}
                                    onToggle={() => toggleRow(row.key)}
                                    leftCandidate={leftCandidate}
                                    rightCandidate={rightCandidate}
                                    mergeResult={mergeResult}
                                />
                            )
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

function StatRow({
    config,
    leftDisplay,
    rightDisplay,
    resultDisplay,
    allEmpty,
    hasLeft,
    hasRight,
    previewing,
    hasResult,
    transferred,
    canExpand,
    isExpanded,
    onToggle,
    leftCandidate,
    rightCandidate,
    mergeResult,
}: {
    config: StatRowConfig
    leftDisplay: string | null
    rightDisplay: string | null
    resultDisplay: string | null
    allEmpty: boolean
    hasLeft: boolean
    hasRight: boolean
    previewing: boolean
    hasResult: boolean
    transferred: boolean
    canExpand: boolean
    isExpanded: boolean
    onToggle: () => void
    leftCandidate?: CandidateData
    rightCandidate?: CandidateData
    mergeResult?: MergeResultData
}) {
    return (
        <>
            <TableRow
                sx={{
                    ...(allEmpty ? { opacity: 0.4 } : undefined),
                    ...(canExpand
                        ? {
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' },
                          }
                        : undefined),
                }}
                onClick={canExpand ? onToggle : undefined}
            >
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {canExpand && (
                            <IconButton size="small" sx={{ mr: 0.5, p: 0 }}>
                                {isExpanded ? (
                                    <KeyboardArrowDown fontSize="small" />
                                ) : (
                                    <KeyboardArrowRight fontSize="small" />
                                )}
                            </IconButton>
                        )}
                        <Typography variant="body2">{config.label}</Typography>
                    </Box>
                </TableCell>
                <TableCell align="right">
                    <ValueCell value={leftDisplay} hasData={hasLeft} />
                </TableCell>
                <TableCell align="right">
                    <ValueCell value={rightDisplay} hasData={hasRight} />
                </TableCell>
                <TableCell align="right" sx={{ bgcolor: 'action.hover' }}>
                    <ResultValueCell
                        value={resultDisplay}
                        previewing={previewing}
                        hasResult={hasResult}
                        transferred={transferred}
                    />
                </TableCell>
            </TableRow>
            {canExpand && isExpanded && (
                <ExpandedRow
                    configKey={config.key}
                    leftCandidate={leftCandidate}
                    rightCandidate={rightCandidate}
                    mergeResult={mergeResult}
                />
            )}
        </>
    )
}

function ValueCell({
    value,
    hasData,
}: {
    value: string | null
    hasData: boolean
}) {
    if (!hasData) {
        return (
            <Typography variant="body2" color="text.secondary">
                —
            </Typography>
        )
    }

    return (
        <Typography
            variant="body2"
            color={value ? 'text.primary' : 'text.secondary'}
            sx={{
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
            }}
        >
            {value ?? '—'}
        </Typography>
    )
}

function ResultValueCell({
    value,
    previewing,
    hasResult,
    transferred,
}: {
    value: string | null
    previewing: boolean
    hasResult: boolean
    transferred: boolean
}) {
    if (previewing) {
        return (
            <Typography variant="body2" color="text.secondary">
                …
            </Typography>
        )
    }

    if (!hasResult) {
        return (
            <Typography variant="body2" color="text.secondary">
                —
            </Typography>
        )
    }

    if (!value) {
        return (
            <Typography variant="body2" color="text.secondary">
                —
            </Typography>
        )
    }

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                maxWidth: 200,
            }}
        >
            <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {value}
            </Typography>
            {transferred && (
                <Typography variant="caption" color="success.main">
                    +
                </Typography>
            )}
        </Box>
    )
}

function getExpandedItems(
    configKey: string,
    candidate: CandidateData | undefined,
    result: MergeResultData | undefined,
    source: 'preview' | 'result'
): readonly { label: string }[] {
    const d = source === 'preview' ? candidate : result
    if (!d) return []

    switch (configKey) {
        case 'startDates': {
            const items =
                source === 'preview'
                    ? (d as CandidateData).startDate
                    : (d as MergeResultData).startDates
            return (items ?? []).map((i) => ({
                label: i.value ?? '(no value)',
            }))
        }
        case 'analysts': {
            const items =
                source === 'preview'
                    ? (d as CandidateData).otherAnalysts
                    : (d as MergeResultData).analysts
            return (items ?? []).map((i) => ({ label: i.analyst }))
        }
        case 'citizenships':
            return (d.citizenships ?? []).map((i) => ({
                label: (i as any).citizenship?.value ?? '(unknown)',
            }))
        case 'cpiPlaced':
            return (d.cpiPlaced ?? []).map((i) => ({
                label: (i as any).cpiPlaced?.value ?? '(unknown)',
            }))
        case 'ethnicities':
            return (d.ethnicities ?? []).map((i) => ({
                label: (i as any).ethnicity?.value ?? '(unknown)',
            }))
        case 'languages':
            return (d.languages ?? []).map((i) => ({
                label: (i as any).language?.value ?? '(unknown)',
            }))
        case 'personalityAttributes':
            return (d.personalityAttributes ?? []).map((i) => ({
                label: (i as any).personalityAttribute?.value ?? '(unknown)',
            }))
        case 'programs': {
            return (d.programs ?? []).map((i) => ({
                label: (i as any).value ?? '(unknown)',
            }))
        }
        case 'realEstateDealExperiences':
            return (d.realEstateDealExperiences ?? []).map((i) => ({
                label:
                    (i as any).realEstateDealExperience?.value ?? '(unknown)',
            }))
        case 'revenueExperiences':
            return (d.revenueExperiences ?? []).map((i) => ({
                label: (i as any).revenueExperience?.value ?? '(unknown)',
            }))
        case 'staffers':
            return ((d as any).staffers ?? []).map((i: any) => ({
                label: i.staffer
                    ? `${i.staffer.firstName} ${i.staffer.lastName}`
                    : '(unknown)',
            }))
        case 'dealSourcingRoles':
            return ((d as any).dealSourcingRoles ?? []).map((i: any) => ({
                label: i.dealSourcingRole?.value ?? '(unknown)',
            }))
        case 'mbaTiers':
            return ((d as any).mbaTiers ?? []).map((i: any) => ({
                label: i.mbaTier?.value ?? '(unknown)',
            }))
        case 'veteranStatuses':
            return ((d as any).veteranStatuses ?? []).map((i: any) => ({
                label: i.veteranStatus?.value ?? '(unknown)',
            }))
        case 'visaStatuses':
            return (d.visaStatuses ?? []).map((i) => ({
                label: (i as any).visaStatus?.value ?? '(unknown)',
            }))
        case 'industryPreferences':
            return ((d as any).industryPreferences ?? []).map((i: any) => ({
                label: `${i.order}. ${i.preferenceIndustry?.value ?? '(unknown)'}`,
            }))
        case 'sectorPreferences':
            return ((d as any).sectorPreferences ?? []).map((i: any) => ({
                label: `${i.order}. ${i.preferenceSector?.value ?? '(unknown)'}`,
            }))
        case 'locationPreferences':
            return ((d as any).locationPreferences ?? []).map((i: any) => ({
                label: `${i.order}. ${i.preferenceLocation?.value ?? '(unknown)'}`,
            }))
        case 'companyPreferences':
            return ((d as any).companyPreferences ?? []).map((i: any) => ({
                label: `${i.order}. ${i.customValue || i.preferenceCompany?.value || '(unknown)'}`,
            }))
        case 'documents':
            return (d.documents ?? []).map((i) => {
                const parts = i.documentKey.split('/')
                const filename = decodeURIComponent(
                    parts[parts.length - 1] || i.documentKey
                )
                return {
                    label: `${filename} · ${new Date(i.createdAt).toLocaleDateString()}`,
                }
            })
        case 'education':
            return (d.education ?? []).map((i) => ({
                label: `${(i as any).college?.value ?? '(unknown)'} · ${i.graduationDate ?? '?'}`,
            }))
        case 'candidateNotes':
            return (d.candidateNotes ?? []).map((i) => ({
                label: `${(i as any).notePreview ? truncate((i as any).notePreview, 50) : '(no preview)'} · ${new Date(i.createdAt).toLocaleDateString()}`,
            }))
        case 'candidateGroupNotes':
            return (d.candidateGroupNotes ?? []).map((i) => ({
                label: `Group Note ${(i as any).groupNoteId?.slice(0, 8) ?? ''}`,
            }))
        case 'references':
            return (d.references ?? []).map((i) => ({
                label: `${(i as any).firstName} ${(i as any).lastName}`,
            }))
        case 'workExperience':
            return (d.workExperience ?? []).map((i) => {
                const title = (i as any).title?.value ?? '(no title)'
                const company = (i as any).company?.value ?? ''
                const dates = `${i.startDate ?? '?'} – ${i.isCurrent ? 'Present' : i.endDate ?? '?'}`
                return {
                    label: `${title}${company ? ` @ ${company}` : ''} · ${dates}`,
                }
            })
        case 'rosters':
            return (d.rosters ?? []).map((i) => ({
                label: (i as any).roster?.rosterName ?? '(unknown roster)',
            }))
        case 'jobBoardsResponses':
            return (d.jobBoardsResponses ?? []).map((i) => {
                const name = (i as any).jobBoard?.name ?? '(unknown)'
                const interested = (i as any).isInterested
                const status =
                    interested === true ? '✓' : interested === false ? '✗' : '?'
                return { label: `${name} (${status})` }
            })
        case 'higTeams':
            return (d.higTeams ?? []).map((i) => ({
                label: (i as any).higTeam?.value ?? '(unknown team)',
            }))
        case 'schedules':
            return ((d as any).schedules ?? []).map((i: any) => ({
                label: i.eventUrl ?? '(no URL)',
            }))
        case 'eppScores':
            return ((d as any).eppScores ?? []).map((i: any) => ({
                label: `${i.eppCategory?.value ?? '(unknown)'}: ${i.score ?? '?'}`,
            }))
        case 'companyClientContacts':
            return ((d as any).companyClientContacts ?? []).map((i: any) => ({
                label: `${i.group} · ${i.contactType}`,
            }))
        case 'rosterClientContacts':
            return ((d as any).rosterClientContacts ?? []).map((i: any) => ({
                label: `Roster contact ${i.rosterId?.slice(0, 8) ?? ''}`,
            }))
        default:
            return []
    }
}

function ExpandedRow({
    configKey,
    leftCandidate,
    rightCandidate,
    mergeResult,
}: {
    configKey: string
    leftCandidate?: CandidateData
    rightCandidate?: CandidateData
    mergeResult?: MergeResultData
}) {
    const leftItems = getExpandedItems(
        configKey,
        leftCandidate,
        undefined,
        'preview'
    )
    const rightItems = getExpandedItems(
        configKey,
        rightCandidate,
        undefined,
        'preview'
    )
    const resultItems = getExpandedItems(
        configKey,
        undefined,
        mergeResult,
        'result'
    )

    return (
        <ExpandedDetailRow
            left={<SimpleValueList items={leftItems} />}
            right={<SimpleValueList items={rightItems} />}
            result={<SimpleValueList items={resultItems} />}
        />
    )
}

function ExpandedDetailRow({
    left,
    right,
    result,
}: {
    left: React.ReactNode
    right: React.ReactNode
    result: React.ReactNode
}) {
    return (
        <TableRow>
            <TableCell />
            <TableCell sx={{ verticalAlign: 'top', p: 1 }}>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>{left}</Box>
            </TableCell>
            <TableCell sx={{ verticalAlign: 'top', p: 1 }}>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>{right}</Box>
            </TableCell>
            <TableCell
                sx={{ verticalAlign: 'top', p: 1, bgcolor: 'action.hover' }}
            >
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>{result}</Box>
            </TableCell>
        </TableRow>
    )
}

function SimpleValueList({ items }: { items: readonly { label: string }[] }) {
    if (items.length === 0) {
        return (
            <Typography variant="caption" color="text.secondary">
                None
            </Typography>
        )
    }

    return (
        <Stack spacing={1} sx={{ py: 0.5 }}>
            {items.map((item, idx) => (
                <Box
                    key={idx}
                    sx={{ borderLeft: 2, borderColor: 'divider', pl: 1 }}
                >
                    <Typography variant="caption" fontWeight={600}>
                        {item.label}
                    </Typography>
                </Box>
            ))}
        </Stack>
    )
}
